// tabs/SessionTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Group, Text, Button, Divider, ActionIcon, Badge,
  Drawer, Stack, TextInput, Textarea, NumberInput, Switch, Tooltip,
  Modal,
} from '@mantine/core';
import { IconPlus, IconTrash, IconPencil, IconRefresh, IconForms } from '@tabler/icons-react';
import {
  useACCourseService, ACCourseSessionModelView,
  CreateACCourseSession, UpdateACCourseSessionRequest,
} from '@actschurch/ccms-sdk';
import { Table, ColorInput } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';
import { SessionFormPage } from '../CustomForms/SpecialSessionFormBuilder';

// ─── Props ────────────────────────────────────────────────────────────────────
interface SessionTabProps {
  courseId: number;
  active: boolean;
  onCourseChange?: () => void;
}

// ─── Empty form shape ─────────────────────────────────────────────────────────
const emptyForm = (): CreateACCourseSession => ({
  courseID:    0,
  name:        '',
  description: '',
  orderNo:     1,
  color:       '',
});

// ─── SessionTab ───────────────────────────────────────────────────────────────
// Lists all session templates for a course.
// Add/Edit via right-side drawer, remove via confirm modal.
// Creating a session with "Is Special Session" on immediately opens the
// custom Form Builder full-screen for that session. Editing does NOT
// auto-open it — isSpecial can't be toggled in edit mode (see below), so
// there's nothing "new" to jump into; use the "Manage Form" row action.
export default function SessionTab({ courseId, active, onCourseChange }: SessionTabProps) {
  const courseService = useACCourseService();

  // Sessions assigned to this course
  const [sessions, setSessions] = useState<ACCourseSessionModelView[]>([]);
  const [loading, setLoading]   = useState(false);

  // Add/Edit drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm]             = useState<CreateACCourseSession>(emptyForm());
  // NEW: whether the session being edited is active (no field in CreateACCourseSession,
  // only relevant/sendable on update)
  const [isActive, setIsActive]     = useState(true);
  const [isSpecial, setIsSpecial] = useState(false);
  const [saving, setSaving]         = useState(false);
  // NEW: which session (if any) we're editing. null => "add" mode.
  const [editingSession, setEditingSession] = useState<ACCourseSessionModelView | null>(null);

  // Confirm-remove modal state
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // NEW: Form Builder full-screen modal — holds the courseSessionID of whichever
  // special session should currently have its custom form open. null = closed.
  const [formBuilderSessionID, setFormBuilderSessionID] = useState<number | null>(null);

  // ── Fetch sessions ─────────────────────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const result = await courseService.getSessions(courseId);
      setSessions(result);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }, [courseId, courseService]);

  // ── Only fetch when tab is active ──────────────────────────────────────────
  useEffect(() => {
    if (active) fetchSessions();
  }, [active, fetchSessions]);

  // ── Open drawer in "add" mode ─────────────────────────────────────────────
  const openDrawer = () => {
    setEditingSession(null);
    setForm({ ...emptyForm(), courseID: courseId });
    setIsActive(true);
    setIsSpecial(false);
    setDrawerOpen(true);
  };

  // ── NEW: Open drawer in "edit" mode, pre-filled from the row ─────────────
  const openEditDrawer = (session: ACCourseSessionModelView) => {
    setEditingSession(session);
    setForm({
      courseID:    courseId,
      name:        session.name ?? '',
      description: session.description ?? '',
      orderNo:     session.orderNo,
      color:       session.color ?? '',
    });
    setIsActive(session.isActive ?? true);
    setIsSpecial(session.isSpecial ?? false)
    setDrawerOpen(true);
  };

  // ── Add or update session ──────────────────────────────────────────────────
  const handleSaveSession = async () => {
    if (!form.name.trim()) {
      toast.error('Validation', 'Session name is required.');
      return;
    }

    setSaving(true);
    try {
      if (editingSession) {
        // UPDATE existing session
        const payload: UpdateACCourseSessionRequest = {
          name:        form.name,
          description: form.description,
          orderNo:     form.orderNo,
          color:       form.color,
          isActive,
          isSpecial,
        };
        await courseService.updateSession(courseId, editingSession.courseSessionID, payload);
        toast.success('Session Updated', `"${form.name}" has been updated.`);

        setDrawerOpen(false);
        setEditingSession(null);
        await fetchSessions();
        onCourseChange?.();

        // NOTE: the "Is Special Session" switch is only shown when creating
        // a session (see below), so isSpecial can't change during an edit —
        // it's always whatever the session already was. Don't auto-open the
        // form builder here; use the "Manage Form" row action instead.
      } else {
        // CREATE new session — capture the created record so we have its
        // real courseSessionID before deciding whether to open the builder.
        const created = await courseService.addSession(courseId, { ...form, isSpecial });
        toast.success('Session Added', `"${form.name}" has been added.`);

        setDrawerOpen(false);
        setEditingSession(null);
        await fetchSessions();
        onCourseChange?.();

        if (isSpecial) {
          // Special session → jump straight into the custom form builder,
          // full-screen, scoped to the session we just created.
          setFormBuilderSessionID(created.courseSessionID);
        }
        // isSpecial === false → creating the session is done, nothing else to do.
      }
    } catch (e) {
      console.error(e);
      toast.error('Error', editingSession ? 'Failed to update session.' : 'Failed to add session.');
    } finally {
      setSaving(false);
    }
  };

  // ── Remove session — opens confirm modal ───────────────────────────────────
  const handleRemoveSession = (session: ACCourseSessionModelView) => {
    const name = session.displayName ?? session.name ?? `Session ${session.courseSessionID}`;
    setConfirmMessage(`Remove session "${name}" from this course?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await courseService.removeSession(courseId, session.courseSessionID);
        await fetchSessions();
        onCourseChange?.();
        toast.warning('Session Removed', `"${name}" has been removed.`);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove session.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Confirm modal */}
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Session"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      {/* Add/Edit session drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={<Text fw={600} size="md">{editingSession ? 'Edit Session' : 'Add Session'}</Text>}
        position="right"
        size="md"
        padding="md"
      >
        <Stack gap="md">
          {/* Session name — required */}
            <TextInput
            label="Session Name"
            placeholder="e.g. Foundations"
            required
            value={form.name}
            onChange={(e) => {
                const value = e.currentTarget.value;
                setForm((f) => ({ ...f, name: value }));
            }}
            />

            {/* Description — optional */}
            <Textarea
            label="Description"
            placeholder="Brief description of this session…"
            minRows={3}
            value={form.description ?? ''}
            onChange={(e) => {
                const value = e.currentTarget.value;
                setForm((f) => ({ ...f, description: value }));
            }}
            />

          {/* Order number */}
          <NumberInput
            label="Order No."
            description="Sequence position within the course"
            value={form.orderNo}
            onChange={(val) => setForm((f) => ({ ...f, orderNo: Number(val) }))}
          />

          {/* Hex color */}
             <ColorInput
        label="Course Colour"
        placeholder="Pick a colour"
        value={form.color || ''}
        onChange={(val) => setForm((f) => ({ ...f, color: val }))}
        format="hex"
      />
         {!editingSession && (
         <Switch
         label='Is Special Session'
         description="Special sessions open the custom form builder immediately after saving"
         checked={isSpecial}
         onChange={(e) => setIsSpecial(e.currentTarget.checked)}
         />
         )}

          {/* NEW: Active toggle — only meaningful (and sent) when editing */}
          {editingSession && (
            <Switch
              label="Active"
              checked={isActive}
              onChange={(e) => setIsActive(e.currentTarget.checked)}
            />
          )}

          <Divider />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSaveSession}>
              {editingSession ? 'Save Changes' : 'Add Session'}
            </Button>
          </Group>
        </Stack>
      </Drawer>

      {/* NEW: Full-screen custom Form Builder for special sessions.
          Opened automatically right after a special session is created,
          or manually via the "Manage Form" row action below. */}
      <Modal
        opened={formBuilderSessionID !== null}
        onClose={() => setFormBuilderSessionID(null)}
        fullScreen
        title="Custom Session Form"
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        {formBuilderSessionID !== null && (
          <SessionFormPage courseSessionID={formBuilderSessionID} courseID={courseId} />
        )}
      </Modal>

      {/* Toolbar */}
      <Group justify="flex-end" mb="md">
         <Tooltip label="Refresh" withArrow>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => fetchSessions()}
                      loading={loading}
                      size="lg"
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
        <Button leftSection={<IconPlus size={16} />} onClick={openDrawer}>
          Add Session
        </Button>
      </Group>

      <Divider mb="md" />

      {/* Sessions table */}
      {sessions.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No sessions added yet.</Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Order</Table.Th>
              <Table.Th>Session Name</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Color</Table.Th>
              <Table.Th>Special Session</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sessions.map((s) => (
              <Table.Tr key={s.courseSessionID}>
                <Table.Td>{s.orderNo}</Table.Td>
                <Table.Td>{s.displayName ?? s.name ?? '—'}</Table.Td>
                <Table.Td>{s.description ?? '—'}</Table.Td>
                <Table.Td>
                  {s.color ? (
                    <Group gap={6} align="center">
                      {/* Color swatch */}
                      <div
                        style={{
                          width: 16, height: 16, borderRadius: 4,
                          backgroundColor: s.color, flexShrink: 0,
                          border: '1px solid rgba(0,0,0,0.12)',
                        }}
                      />
                      <Text size="xs">{s.color}</Text>
                    </Group>
                  ) : '—'}
                </Table.Td>
                <Table.Td>
                    <Badge color={s.isSpecial ? 'green' : 'red'} variant="light">
                    {s.isSpecial ? 'Yes' : 'No'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={s.isActive ? 'green' : 'red'} variant="light">
                    {s.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {s.isSpecial && (
                      <Tooltip label="Manage custom form" withArrow>
                        <ActionIcon
                          variant="light"
                          color="grape"
                          onClick={() => setFormBuilderSessionID(s.courseSessionID)}
                        >
                          <IconForms size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <ActionIcon variant="light" onClick={() => openEditDrawer(s)}>
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => handleRemoveSession(s)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </>
  );
}